/**
 * Demo çalışma alanı sözleşmesi (M3 K-M3-01; v3.3 görev DEMO — demo kabul alanı). Belge: docs/spec/castinflow/demo-workspace.md.
 *
 * Demo, kayıt sırasında seçilen (ya da operatörün açtığı) ayrı çalışma alanıdır; örnek veri yalnız orada ve yalnız yöneticinin
 * açık eylemiyle yüklenir, tekrar yükleme çoğaltmaz. Demo'da e-posta, davet, ileti gönderimi, web kancası ve sağlayıcı çağrısı
 * yoktur.
 *
 * Örnek veri SÜRÜMLÜDÜR. Sürüm 1: şirket, kişi, ürün, aday, fırsat, satış belgesi, sözleşme. Sürüm 2 (v3.3): kaynak ve
 * kampanya, dağıtım kuralı, reklam formundan gelen aday (olay + atama izi), demo kanalında okunmamış konuşmalar, takip görevi,
 * aday → fırsat → teklif → sipariş zinciri ve sentetik reklam harcaması. Sürüm 1 yüklü çalışma alanı sürüm 2'ye TAMAMLANIR
 * (aynı uç, aynı düğme); sürüm 2 adımları tek tek "varsa atla" kuralıyla çalıştığı için yarıda kalan yükseltme sürdürülebilir.
 * Sürüm 3 (v4.5): proje ve hizmet teslimi örnekleri — iki örnek hizmet ürünü, kabul edilmiş "Demo –" teklifi, ondan bir proje ve
 * bir dönemsel hizmet (aşama, teslimat onayı, içerik isteği, iş kaydı, sentetik finans satırı ve teknik görev bağı). Teslim modülü
 * (`delivery`) etkin değilse adım `module_off` sebebiyle atlanır; modül açıldıktan sonra "atlanan örnekleri yeniden dene" ekler.
 * v4.6: modül kapalı kaldıkça bu atlama uyarı değil bilgidir — durum ucu onu `waitingForModule` ile ayrıca bildirir.
 * Sürüm 2 yüklü çalışma alanı sürüm 3'e aynı modelle tamamlanır (yalnız sürüm 3 adımları çalışır).
 * Sürüm 4 (v4.7): destek örnekleri — örnek şirkete ürün/hizmet hakkı, demo kanalında iki konuşma ve onlardan açılmış iki destek
 * talebi (biri açık, hak ve sentetik dış görev bağıyla; biri müşteri yanıtıyla bir kez yeniden açılıp tekrar çözülmüş). Teslim
 * modülüne dayanır (`support` adımı, `module_off` modeli sürüm 3 ile aynı).
 */
import { z } from 'zod';
import { PLAN_MODULES } from './modules.js';
/** Sunucunun yüklediği güncel örnek veri sürümü */
export const DEMO_SEED_LATEST_VERSION = 4;
export const DEMO_SEED_STATUSES = ['loading', 'loaded', 'failed'];
/**
 * Atlanan örnek veri adımları (v3.3). Kullanılmış bir demo çalışma alanında ürünün iş kuralı bir adımı reddedebilir (kota,
 * sınır, silinmiş / pasif kayıt, kullanıcının aynı yerde kendi kaydı). Yükleme düşmez: adım atlanır ve yükleme sayılarına
 * `skipped_<adım>` olarak yazılır; arayüz bunu kullanıcıya söyler. Mevcut kayıtlara dokunulmaz.
 *
 * v3.4: kayıtlı değer SEBEP NUMARASIDIR (`DEMO_SKIP_REASONS` sırası, 1'den başlar): `1` = `unspecified` — v3.3'ün yazdığı
 * değer olduğu için eski kayıt aynen okunur ve `> 0` denetimi (atlandı mı?) değişmez. Yeni sebep yalnız dizinin SONUNA eklenir.
 * Migration gerekmez: sayılar zaten kalıcı yükleme kaydındadır (`demo_seed_runs.counts`).
 */
export const DEMO_SKIP_PREFIX = 'skipped_';
export const DEMO_SKIP_KEYS = [
    'campaign',
    'distribution_rule',
    'lead_intake',
    'channel',
    'quick_reply',
    'conversation_link',
    'task',
    'sales_chain',
    /** Sürüm 3: proje ve hizmet teslimi örnekleri (ürünler, teklif, proje, dönemsel hizmet ve alt öğeleri; tek adım) */
    'delivery',
    /** Sürüm 4: destek örnekleri (ürün/hizmet hakkı, iki konuşma, iki talep, hak ve sentetik görev bağları; tek adım) */
    'support',
];
export const DEMO_SKIP_LABELS = {
    campaign: 'örnek kampanyalar',
    distribution_rule: 'örnek dağıtım kuralı',
    lead_intake: 'reklam kaynaklı örnek adaylar',
    channel: 'örnek mesajlaşma kanalı ve konuşmalar',
    quick_reply: 'örnek hazır yanıt',
    conversation_link: 'örnek konuşmanın adayla ilişkisi',
    task: 'örnek takip görevi',
    sales_chain: 'örnek aday → fırsat → teklif → sipariş zinciri',
    delivery: 'örnek proje ve hizmet teslimi kayıtları',
    support: 'örnek destek talepleri ve ürün/hizmet hakları',
};
/**
 * Bir adımın neden atlandığı (kapalı küme; sıra = kayıtlı sebep numarası − 1, yalnız sona eklenir):
 *   unspecified     ürünün iş kuralı reddetti (ayrıntısı ayrıştırılamadı; v3.3 kayıtları)
 *   plan_limit      plan ya da kullanım sınırı dolu
 *   record_missing  adımın dayandığı örnek kayıt silinmiş, pasif ya da bulunamadı
 *   prerequisite    önceki bir örnek adım eklenemediği için bu adım yapılamadı
 *   conflict        aynı yerde sizin açtığınız bir kayıt var; dokunulmadı
 *   not_allowed     yetki, onay ya da çalışma alanı durumu izin vermedi
 *   invalid         alan kuralları (zorunlu alan, değer sınırı) kabul etmedi
 *   temporary       geçici bir durum (sunucu yoğun / hazır değil); biraz sonra yeniden denenebilir
 *   approval_required  (v4.7) kuruluşun teklif onay kuralı örnek teklif için onay istiyor ve onay tamamlanamadı (ör. talep
 *                   reddedildi, geri çekildi ya da başka birinin bekleyen talebi var); örnek kayıt onaysız ilerletilmez
 *   module_off      adımın dayandığı modül (sürüm 3: proje ve hizmet teslimi, `delivery`) bu çalışma alanında etkin değil;
 *                   modül hakkı yalnız operatörce verilir — açıldıktan sonra yeniden denenebilir
 */
export const DEMO_SKIP_REASONS = [
    'unspecified',
    'plan_limit',
    'record_missing',
    'prerequisite',
    'conflict',
    'not_allowed',
    'invalid',
    'temporary',
    'module_off',
    'approval_required',
];
/** Kullanıcı dilinde kısa neden (teknik kod değil) */
export const DEMO_SKIP_REASON_TEXT = {
    unspecified: 'ürünün iş kuralı bu örneğin eklenmesine izin vermedi',
    plan_limit: 'plan ya da kullanım sınırı dolu',
    record_missing: 'dayandığı örnek kayıt silinmiş, pasif ya da bulunamadı',
    prerequisite: 'önceki bir örnek adım eklenemediği için yapılamadı',
    conflict: 'aynı yerde sizin açtığınız bir kayıt olduğu için dokunulmadı',
    not_allowed: 'yetki, onay ya da çalışma alanı durumu izin vermedi',
    invalid: 'alan kuralları (zorunlu alan, değer sınırı) bu örneği kabul etmedi',
    temporary: 'sunucu şu an yanıt veremedi; biraz sonra yeniden deneyebilirsiniz',
    module_off: 'Proje ve hizmet modülü bu çalışma alanında açık değil',
    approval_required: 'kuruluşunuzun teklif onay kuralı bu örnek teklif için onay istiyor ve onay tamamlanamadı',
};
/**
 * Sürümlerin getirdiği yeni örnekler (kullanıcı dilinde kısa; "Örnek verileri güncelle" açıklaması). Sürüm 1 temel içeriktir ve
 * güncellemede hiç önerilmez; yeni sürüm eklenince buraya da eklenir.
 */
export const DEMO_SEED_VERSION_CONTENT = {
    2: 'reklam kaynaklı adaylar, konuşmalar, takip görevi ve kampanya raporu',
    3: 'proje ve hizmet teslimi örnekleri (proje, aylık hizmet, teslimat onayı ve içerik isteği)',
    4: 'destek örnekleri (ürün/hizmet hakkı, konuşmadan açılmış açık ve çözülmüş talepler, sentetik dış görev bağı)',
};
/** Tamamlanmış sürümden güncel sürüme güncellemenin getireceği yeni örnekler (sürüm sırasında; bilinmeyen sürüm atlanır) */
export function demoUpgradeContent(completedVersion, latestVersion) {
    const out = [];
    for (let v = Math.max(completedVersion, 1) + 1; v <= latestVersion; v += 1) {
        const c = DEMO_SEED_VERSION_CONTENT[v];
        if (c !== undefined)
            out.push(c);
    }
    return out;
}
/** Kayıt değeri (yazılan sayı) ← sebep */
export function demoSkipValueOf(reason) {
    return DEMO_SKIP_REASONS.indexOf(reason) + 1;
}
/** Sebep ← kayıt değeri; tanınmayan / eski değer `unspecified` */
export function demoSkipReasonOfValue(value) {
    return DEMO_SKIP_REASONS[value - 1] ?? 'unspecified';
}
/** Yükleme sayılarından atlanan adımlar (sabit sırada) */
export function demoSkippedSteps(counts) {
    return DEMO_SKIP_KEYS.filter((k) => (counts[`${DEMO_SKIP_PREFIX}${k}`] ?? 0) > 0);
}
export const demoSkippedStepSchema = z.object({
    step: z.enum(DEMO_SKIP_KEYS),
    reason: z.enum(DEMO_SKIP_REASONS),
});
/** Atlanan adımlar ve nedenleri (sabit adım sırasında) */
export function demoSkippedList(counts) {
    return demoSkippedSteps(counts).map((step) => ({
        step,
        reason: demoSkipReasonOfValue(counts[`${DEMO_SKIP_PREFIX}${step}`] ?? 0),
    }));
}
/**
 * v4.6: modüle dayanan atlama adımı → dayandığı modül. Sebebi `module_off` olan atlama, modül HÂLÂ etkin değilken bir uyarı değil
 * "modül açılınca eklenebilir" bilgisidir: modül hakkı yalnız operatör kararıdır ve yeniden deneme o sürece bir şey ekleyemez.
 * Durum ucu bunları `waitingForModule` alanında ayrıca bildirir; `skipped` biçimi değişmez.
 */
export const DEMO_SKIP_STEP_MODULE = {
    delivery: 'delivery',
    support: 'delivery',
};
export const demoWaitingStepSchema = z.object({
    step: z.enum(DEMO_SKIP_KEYS),
    /** Açılması beklenen modül */
    module: z.enum(PLAN_MODULES),
});
/** Kısa, kullanıcı dilinde tek satır: "örnek kampanyalar — plan ya da kullanım sınırı dolu" */
export function demoSkipLine(s) {
    return `${DEMO_SKIP_LABELS[s.step]} — ${DEMO_SKIP_REASON_TEXT[s.reason]}`;
}
export const demoStatusSchema = z.object({
    isDemo: z.boolean(),
    seed: z
        .object({
        status: z.enum(DEMO_SEED_STATUSES),
        /** Yüklemenin hedef sürümü (yüklenen / yüklenmiş sürüm) */
        version: z.number().int(),
        startedAt: z.string(),
        finishedAt: z.string().nullable(),
        counts: z.record(z.string(), z.number().int()),
        /** Tamamlanmış en yüksek sürüm (0 = hiçbir sürüm tamamlanmadı). Eski sunucu yanıtında alan yoktur */
        completedVersion: z.number().int().optional(),
    })
        .nullable(),
    /** Sunucunun güncel örnek veri sürümü. Eski sunucu yanıtında alan yoktur (yükseltme önerilmez) */
    latestVersion: z.number().int().optional(),
    /**
     * `POST /demo/load` şu an bir şey yükleyebilir mi: hiç yüklenmemiş, eski sürüm yüklü (tamamlama) ya da sürüm yükseltmesi
     * yarıda kalmış (sürdürme). Yükleme sürüyorsa ya da ilk yükleme yarıda kaldıysa `false`.
     */
    loadable: z.boolean().optional(),
    /**
     * v3.4: yüklemede ATLANAN örnek adımlar ve nedenleri (yalnız demo çalışma alanında; atlanan yoksa boş dizi). Yükleme bittikten
     * sonra da kalıcıdır: yeniden deneme başarıyla tamamlayana kadar durum ucu bunları döndürür. Eski sunucu yanıtında alan yoktur
     * (arayüz o zaman `seed.counts` içindeki `skipped_*` sayılarından türetir).
     */
    skipped: z.array(demoSkippedStepSchema).optional(),
    /**
     * v3.4: `POST /demo/retry-skipped` şu an çalışabilir mi — güncel sürüm yüklü, atlanan adım var ve başka bir yükleme / deneme
     * sürmüyor. Yalnız demo çalışma alanında döner. v4.6: atlananların HEPSİ modül bekliyorsa (`waitingForModule`) `false` —
     * modül açılmadan deneme bir şey ekleyemez; modül açılınca aynı kayıt yeniden `true` olur.
     */
    retryable: z.boolean().optional(),
    /**
     * v4.6: atlananlardan, dayandığı modül (bugün yalnız `delivery`) bu çalışma alanında HÂLÂ etkin olmadığı için `module_off`
     * sebebiyle bekleyenler (bu adımlar `skipped` içinde de aynen durur). Arayüz bunları uyarı olarak değil, "Ayrıntılar"da kısa
     * bilgi olarak gösterir. Yalnız demo çalışma alanında döner (bekleyen yoksa boş dizi); eski sunucu yanıtında alan yoktur
     * (arayüz o zaman bütün atlananları önceki gibi uyarı olarak gösterir).
     */
    waitingForModule: z.array(demoWaitingStepSchema).optional(),
    /**
     * v3.8.1: bu ortamda hesap güvenliği e-postası (demo alanında YALNIZ parola sıfırlama) gerçekten gönderilebilir mi — sistem
     * postası gerçek gönderim kipinde mi (`on`) yoksa kapalı / sınama alıcısında mı (`off`). Yalnız demo çalışma alanında döner;
     * yalnız şerit metni içindir. Eski sunucu yanıtında alan yoktur.
     */
    accountSecurityMail: z.enum(['on', 'off']).optional(),
});
/**
 * `POST /demo/retry-skipped` yanıtı (v3.4). Kullanıcının açık eylemiyle yalnız ATLANAN adımlar yeniden denenir: var olan kayıtlar
 * değişmez / silinmez / çoğalmaz, dış etki yoktur. İkinci çağrı aynı sonucu verir (eklenemeyen adım yine atlanmış görünür).
 *   attempted  bu çağrıda denenen adımlar (atlanan yoksa boş)
 *   resolved   denenip bu kez eklenen adımlar
 */
export const demoRetryResultSchema = z.object({
    attempted: z.array(z.enum(DEMO_SKIP_KEYS)),
    resolved: z.array(z.enum(DEMO_SKIP_KEYS)),
    status: demoStatusSchema,
});
/** `POST /demo/load` 409 `details.reason` */
export const DEMO_LOAD_REFUSAL_REASONS = ['not_demo', 'loading', 'incomplete'];
/** `POST /demo/retry-skipped` 409 `details.reason` (`not_loaded`: güncel sürüm henüz yüklenmemiş — önce "Örnek verileri yükle") */
export const DEMO_RETRY_REFUSAL_REASONS = ['not_demo', 'loading', 'not_loaded'];
// ---------------------------------------------------------------------------------------------------------------------
// Etiketler (görünen metin; arayüz ve sunucu aynı sabitleri kullanır)
// ---------------------------------------------------------------------------------------------------------------------
/** Demo kaydının kısa işareti (rozet). Bağlantı DURUMU değildir: durum sözlüğünden bağımsız ayrı bir işarettir */
export const DEMO_BADGE = 'Demo';
/** Sentetik harcama ve performans verisinin kaynağı (rapor ve harcama ekranı) */
export const DEMO_DATA_LABEL = 'Demo verisi';
/** Demo bağlantısının açıklaması (Bağlantılar ekranı) */
export const DEMO_CONNECTION_NOTE = 'Demo bağlantısı: hiçbir sağlayıcı hesabına bağlı değildir, yalnız örnek verileri taşır. Yönetim eylemi yoktur.';
/** Demo kanalında yanıt yazarken gösterilen bilgi */
export const DEMO_SEND_NOTE = 'Demo alanında ileti gönderilmez: yazdığınız yanıt konuşmada görünür ama müşteriye ulaşmaz. Hazır yanıtları ve iç notları deneyebilirsiniz.';
/** Sentetik harcamanın açıklaması */
export const DEMO_SPEND_NOTE = 'Bu harcama ve performans rakamları örnek veridir; hiçbir reklam platformundan okunmamıştır.';
/**
 * Demo kanalının açıklaması (gelen kutusu, konuşma, kanal listesi, bağlantılar). Kanal adı ("WhatsApp (Demo)") sunucuda tutulan
 * örnek veridir; bu metin ise resmî ya da canlı bir hesap izlenimi vermesin diye marka adı taşımaz.
 */
export const DEMO_CHANNEL_NOTE = 'Demo kanalı — gerçek bir mesajlaşma hesabına bağlı değildir; ileti gönderilmez.';
/**
 * Demo kampanya raporunda kaynak kapsamasının neden %100 olmadığı. Kapsama bilinçli olarak eksik bırakılmış örnektir:
 * eksik harcama verisinin raporda nasıl göründüğünü göstermek içindir. Yalnız demo çalışma alanında (`spend.demo`) ve
 * kapsama %100'ün altındayken gösterilir.
 */
export const DEMO_COVERAGE_NOTE = 'Bu çalışma alanında kaynak kapsaması bilerek %100 değildir: kapsama, harcaması bilinen kampanyalardaki adayların payıdır; örnek sosyal medya kampanyasının harcaması yoktur ve elle girilen örnek adayların kampanyası yoktur. Raporun eksik veriyi nasıl gösterdiğini görmeniz için böyle bırakılmıştır.';
/** Demo bağlantısına yönetim eylemi denendiğinde (409 `details.reason = demo_connection`) */
export const DEMO_CONNECTION_REFUSAL = 'Demo bağlantısı yönetilemez: gerçek bir sağlayıcıya bağlı değildir';
/** Demo çalışma alanında gerçek sağlayıcı hesabı bağlama denendiğinde (409 `details.reason = demo_workspace`) */
export const DEMO_REAL_CONNECT_REFUSAL = 'Demo çalışma alanında gerçek sağlayıcı hesabı bağlanamaz; bağlama akışını gerçek çalışma alanınızda kullanın';
// ---------------------------------------------------------------------------------------------------------------------
// Sentetik kimlikler (gerçek bir hesaba, hatta ya da kişiye denk GELMEZ)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Demo konuşmalarının müşteri tanıtıcısı: `demo-musteri-<n>` (tür `external`). Telefon numarası DEĞİLDİR — gerçek bir hatta
 * denk gelme riski sıfırdır (M3 K-M3-01 kuralı: örnek veride telefon numarası yoktur) ve kişi eşleştirme çekirdeğine girmez.
 */
export const DEMO_HANDLE_RE = /^demo-musteri-[0-9]{1,3}$/;
export function demoCustomerHandle(n) {
    return `demo-musteri-${String(n)}`;
}
//# sourceMappingURL=demo.js.map